/**
 * Automation Store
 *
 * Core data store for the automation script editor.
 * State shape matches the real request_payload.json from S3.
 *
 * @module store/automation/automationStore
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import logger from '../../shared/services/logging/logger';
import { remapVarRefsInStep } from '../../features/automationScripts/utils/variableRefUtils';
import {
  normalizeStepType,
  CONDITION_TYPE_FIELDS,
  DEFAULT_WEB_UI_BROWSER_TYPE,
  DEFAULT_IOS_BROWSER,
  DEFAULT_ANDROID_BROWSER,
  normalizeWebUiBrowserType,
  stripUnsupportedBrowserOptions,
  findUnsupportedBrowserOptionKeys,
} from '../../features/automationScripts/config/automationConstants';

// ── Helpers ─────────────────────────────────────────────────────

const generateId = () => Math.random().toString(36).slice(2, 10);

// Default device configs for a freshly-created / imported mobile step. Kept in sync
// with the editor defaults in useMobileStepEditor — a standalone mobile step needs a
// complete config so it is both editable and runnable.
const DEFAULT_ANDROID_DEVICE_CONFIG = {
  connectionType: 'emulator',
  platformName: 'Android',
  deviceName: 'Pixel_8',
  app: '',
  appPackage: '',
  appActivity: '',
  browserName: '',
  startUrl: '',
  noReset: false,
  fullReset: false,
};

const DEFAULT_IOS_DEVICE_CONFIG = {
  connectionType: 'simulator',
  platformName: 'iOS',
  deviceName: 'iPhone_15',
  platformVersion: '18.6',
  app: '',
  bundleId: '',
  browserName: '',
  noReset: false,
  fullReset: false,
};

/**
 * Generate a unique group name by appending _1, _2, etc. if name already exists.
 * @param {string} baseName - Desired group name
 * @param {Array} existingGroups - Current stepGroups array
 * @returns {string} Unique name
 */
const ensureUniqueGroupName = (baseName, existingGroups) => {
  const existingNames = new Set(existingGroups.map(g => g.name));
  if (!existingNames.has(baseName)) return baseName;
  let counter = 1;
  while (existingNames.has(`${baseName}_${counter}`)) counter++;
  return `${baseName}_${counter}`;
};

// Actions that do not use element locators (identifierType should be stripped from payload)
const NO_LOCATOR_ACTIONS = new Set([
  'Open URL', 'Go Back', 'Go Forward', 'Refresh Page',
  'Release Mouse Button', 'Press Key Down', 'Release Key Up', 'Press Keyboard Key',
  'Wait', 'Wait for All Loaders to Disappear',
  'Get Page Title', 'Get Current URL',
  'Accept Alert/Popup', 'Dismiss/Cancel Alert', 'Get Alert Text', 'Type into Alert',
  'Scroll Page Up', 'Scroll Page Down',
  'Switch to Window/Tab', 'Switch to Main Content', 'Switch to Parent Frame',
  'Close Current Window/Tab',
  'Execute JavaScript', 'Maximize Browser Window', 'Minimize Browser Window', 'Set Window Size',
  'Take Screenshot',
  // Assertions — no element locator needed
  'Hard Assert Equals', 'Hard Assert Not Equals', 'Hard Assert True',
  'Hard Assert False', 'Hard Assert Null', 'Hard Assert Not Null',
  'Soft Assert Equals', 'Soft Assert Not Equals', 'Soft Assert True',
  'Soft Assert False', 'Soft Assert Null', 'Soft Assert Not Null',
  // Stop & Exit — any locator they need lives inside `condition`, not on the action
  'Break Loop', 'End Permutation',
]);

// Map frontend identifierType values → backend enum values
const IDENTIFIER_TYPE_MAP = { css: 'cssSelector', class: 'className' };

// Normalize platform-specific mobile action payloads at save time, recursively
// (including nested control-flow bodies):
//   • iOS "Set System Date Time" carries no time zone.
//   • "Launch App" activates the app by its platform identifier only — bundleId on
//     iOS, appPackage on Android — and never needs an app activity.
const normalizeMobileActionForPlatform = (action, platform) => {
  if (!action || typeof action !== 'object') return action;
  const next = { ...action };
  if (next.action === 'Set System Date Time' && platform === 'iOS') {
    delete next.systemTimeZone;
  }
  if (next.action === 'Launch App') {
    delete next[platform === 'iOS' ? 'appPackage' : 'bundleId'];
    delete next.appActivity;
  }
  for (const key of ['then', 'else', 'actions']) {
    if (Array.isArray(next[key])) next[key] = next[key].map((a) => normalizeMobileActionForPlatform(a, platform));
  }
  return next;
};

/**
 * Create the default sampleRequestContent / sampleResponseContent
 * that every API step request MUST have.
 * @param {string} sharedId - Shared UUID so request/response files can be paired
 */
const createSampleFileRef = (projectPath, projectId, suffix, sharedId) => ({
  name: `${projectId}_${sharedId}_${suffix}`,
  type: 'json',
  s3Link: projectPath
    ? `${projectPath}/${projectId}_${sharedId}_${suffix}.json`
    : '',
});

/**
 * Build one default API request object (with linked sample files).
 * Both files share the same UUID for easier reference.
 */
const createDefaultApiRequest = (alias, projectPath, projectId) => {
  const sharedId = generateId();
  return {
    sampleRequestContent: createSampleFileRef(projectPath, projectId, 'request', sharedId),
    sampleResponseContent: createSampleFileRef(projectPath, projectId, 'response', sharedId),
    requestType: 'json',
    headers: [],
    fieldsToExclude: [],
    assertions: [],
    fieldsToExtract: {},
    formData: [],
  };
};

// ── Initial state ───────────────────────────────────────────────

const INITIAL_STATE = {
  // Script metadata
  projectId: '',
  projectName: '',
  projectPath: '',
  tenantId: '',
  s3Bucket: '',
  scriptName: '',

  // Data sources (from tests[0].dataSources)
  dataSources: [],

  // Test files (from tests[0].testFiles)
  testFiles: [],

  // Steps (from tests[0].steps)
  steps: [],

  // Test variables (from tests[0].variables — NOT globalVariables)
  variables: [],

  // Data source column names (populated after CSV load)
  requestDataColumns: [],
  responseDataColumns: [],

  // Environments (from environments.csv)
  environments: [],
  activeEnvId: null,

  // Settings
  stopOnFailure: false,

  // Step groups (from grouped_steps.json — separate from request_payload)
  stepGroups: [],

  // Object Bank (from Automation Scripts/object_bank.json — project-level, shared across scripts)
  objectBank: [],

  // Loading
  isLoading: false,
  loadingError: null,
  isHydrated: false,

  // Browser options removed on hydrate because the step's browser does not support them.
  // The page reads this once after load and saves the repaired script back.
  browserOptionRepairs: [],
};

// ── Store ───────────────────────────────────────────────────────

export const useAutomationStore = create((set, get) => ({
  ...INITIAL_STATE,

  // ── Hydration ───────────────────────────────────────────────

  /**
   * Populate store from request_payload.json + environments.csv data.
   * @param {Object} payload - Parsed request_payload.json
   * @param {Array}  environments - Parsed environments array [{environment, base_url, active}]
   */
  hydrateFromPayload: (payload, environments = [], fallbackBucket = '') => {
    // Debug: log the actual payload structure so we can see what S3 returns
    logger.debug('hydrateFromPayload: raw payload inspection', {
      topLevelKeys: Object.keys(payload),
      hasTests: !!payload.tests,
      testsIsArray: Array.isArray(payload.tests),
      testsLength: payload.tests?.length,
      test0Keys: payload.tests?.[0] ? Object.keys(payload.tests[0]) : 'N/A',
      test0StepCount: payload.tests?.[0]?.steps?.length,
      test0DataSourceCount: payload.tests?.[0]?.dataSources?.length,
      payloadProjectId: payload.projectId,
      payloadProjectName: payload.projectName,
    });

    const test = payload.tests?.[0] || {};

    // Parse environments from CSV rows
    const envs = environments.map((env, i) => ({
      id: `env-${i}-${Date.now()}`,
      name: env.environment || env.name || '',
      baseUrl: env.base_url || env.baseUrl || '',
      active: env.active === true || env.active === 'true',
    }));

    // Find the first active environment
    const activeEnv = envs.find(e => e.active) || envs[0] || null;

    // Options a saved script carries that its browser has no use for — collected while steps
    // are normalized so the page can repair the stored file, see browserOptionRepairs below.
    const browserOptionRepairs = [];

    // Normalize steps — preserve existing id, fall back to alias.
    // stepType casing is normalized via the shared helper (backend sends "mobile",
    // the UI checks "Mobile").
    const steps = (test.steps || []).map((step, i) => {
      const normalized = {
        ...step,
        id: step.id || step.alias || `step-${i + 1}`,
        order: i + 1,
        stepType: normalizeStepType(step.stepType),
        // Give step variables a stable UI id (the payload stores them without one) so
        // the editor can key/edit/remove individual rows. Stripped again on save.
        ...(Array.isArray(step.variables) && step.variables.length > 0 && {
          variables: step.variables.map((v, vi) => ({ ...v, id: v.id || `sv-${i}-${vi}-${Date.now()}` })),
        }),
      };

      if (normalized.stepType !== 'Web-UI') return normalized;

      // Repair the browser config on the way in.
      //
      // The per-browser option matrix is not uniform and it can change on the engine side
      // without a frontend release, so a script saved a while ago can hold an option its
      // browser has no use for — most likely disableInfobars on a step that has since been
      // moved to Firefox. Leaving it would send a key the browser cannot honour.
      //
      // Only UNSUPPORTED keys go. A supported option is left exactly as saved, true or false
      // alike: false is a real instruction ("do not suppress"), not a leftover, and rewriting
      // it would change what the script does.
      const browserType = normalizeWebUiBrowserType(normalized.browserType);
      const removed = findUnsupportedBrowserOptionKeys(normalized.browserOptions, browserType);
      if (removed.length === 0) return { ...normalized, browserType };

      browserOptionRepairs.push({
        stepOrder: normalized.order,
        stepName: normalized.name,
        browserType,
        removed,
      });
      return {
        ...normalized,
        browserType,
        browserOptions: stripUnsupportedBrowserOptions(normalized.browserOptions, browserType),
      };
    });

    // Normalize data sources
    const dataSources = (test.dataSources || []).map(ds => ({
      ...ds,
      id: ds.id || `ds-${generateId()}`,
    }));

    set({
      projectId: payload.projectId || '',
      projectName: payload.projectName || '',
      projectPath: payload.projectPath || '',
      tenantId: payload.tenantId || '',
      s3Bucket: payload.s3Bucket || fallbackBucket || '',
      scriptName: test.name || '',
      dataSources,
      testFiles: test.testFiles || [],
      steps,
      variables: (test.variables || []).map((v, i) => ({ id: `var-${i}-${Date.now()}`, ...v })),
      environments: envs,
      activeEnvId: activeEnv?.id || null,
      stopOnFailure: payload.stopOnFailure === true || payload.stopOnFailure === 'true',
      isHydrated: true,
      isLoading: false,
      // Read once by the page after load, to write the repaired script back. Held in the store
      // rather than returned so it survives the load being kicked off from anywhere.
      browserOptionRepairs,
    });

    logger.info('Automation store hydrated', {
      projectId: payload.projectId,
      scriptName: test.name,
      stepCount: steps.length,
      dataSourceCount: dataSources.length,
      envCount: envs.length,
    });

    if (browserOptionRepairs.length > 0) {
      logger.info('Removed browser options the step browser does not support', {
        repairs: browserOptionRepairs.map(r => `Step ${r.stepOrder} (${r.browserType}): ${r.removed.join(', ')}`),
      });
    }
  },

  setLoading: (isLoading, loadingError = null) =>
    set({ isLoading, loadingError }),

  // ── Step CRUD ─────────────────────────────────────────────────

  addStep: (stepType) => {
    const { steps, projectPath, projectId } = get();
    const newOrder = steps.length + 1;
    const alias = `step${newOrder}`;

    const typeLabel = stepType === 'Api' ? 'API'
      : stepType === 'Web-UI' ? 'Web UI'
        : stepType;

    const base = {
      id: `step-new-${Date.now()}`,
      order: newOrder,
      name: `New ${typeLabel} Step`,
      description: '',
      stepType,
      alias,
      variables: [],
    };

    let newStep;

    if (stepType === 'Api') {
      // API steps MUST auto-create sampleRequest + sampleResponse file refs
      newStep = {
        ...base,
        endpoint: {
          url: '',
          protocol: 'HTTP_POST',
          parameters: {},
          baseUrl: '',
          path: '',
          absolutePath: '',
        },
        requests: [createDefaultApiRequest(alias, projectPath, projectId)],
        extractedFields: [],
        status: null,
      };
    } else if (stepType === 'Web-UI') {
      newStep = {
        ...base,
        sessionId: `session-${newOrder}`,
        browserType: DEFAULT_WEB_UI_BROWSER_TYPE,
        browserOptions: {
          disablePasswordManager: false,
          disableNotifications: false,
          disablePopups: false,
          disableInfobars: false,
          disableGeolocation: false,
          blockAds: false,
        },
        hasPassed: null,
        hasExecutedSuccessfully: null,
        uiSteps: [],
        stopOnFailure: true,
      };
    } else if (stepType === 'Database') {
      newStep = {
        ...base,
        assertions: [],
        fieldsToExtract: {},
        connection: {
          databaseType: 'POSTGRESQL',
          host: '',
          port: 5432,
          databaseName: '',
          schema: 'public',
          credentials: { username: '', password: '' },
          operation: { type: 'SQL', expression: '' },
        },
      };
    } else if (stepType === 'Mobile') {
      newStep = {
        ...base,
        sessionId: `session-${newOrder}`,
        mobilePlatform: 'Android',
        deviceConfig: { ...DEFAULT_ANDROID_DEVICE_CONFIG },
        mobileSteps: [],
      };
    } else {
      newStep = base;
    }

    set({ steps: [...steps, newStep] });
    logger.debug('Step added', { stepType, alias });
    return newStep;
  },

  removeStep: (stepId) =>
    set(state => {
      const filtered = state.steps.filter(s => s.id !== stepId);
      return {
        steps: filtered.map((s, i) => ({ ...s, order: i + 1, alias: `step${i + 1}` })),
      };
    }),

  addStepFromSource: (sourceStep) => {
    const { steps, projectPath, projectId } = get();
    const newOrder = steps.length + 1;
    const alias = `step${newOrder}`;

    let clone = {
      ...JSON.parse(JSON.stringify(sourceStep)),
      id: `step-reuse-${Date.now()}`,
      order: newOrder,
      alias,
      // Source steps come straight from the stored payload, where mobile steps are
      // persisted lowercase ("mobile"). Canonicalize so the editor recognizes the type.
      stepType: normalizeStepType(sourceStep.stepType),
    };

    // Regenerate sample file refs for API steps
    if (clone.stepType === 'Api' && clone.requests) {
      clone.requests = clone.requests.map(req => {
        const sharedId = generateId();
        return {
          ...req,
          sampleRequestContent: createSampleFileRef(projectPath, projectId, 'request', sharedId),
          sampleResponseContent: createSampleFileRef(projectPath, projectId, 'response', sharedId),
        };
      });
    }

    // Session-capable steps: clear the imported session link. The original session
    // belongs to the source script and would dangle here, so we leave it blank for
    // the user to re-assign (or leave blank to start a fresh session).
    if (clone.stepType === 'Web-UI' || clone.stepType === 'Mobile') {
      clone.sessionId = '';
    }

    // Mobile: a session-continuation step carries no real device config of its own
    // (it inherits the live session from the step that started it). Imported as a
    // standalone step it needs a complete device config to be editable + runnable, so
    // seed platform defaults over whatever minimal config the payload carried.
    if (clone.stepType === 'Mobile') {
      const platform = clone.mobilePlatform === 'iOS' ? 'iOS' : 'Android';
      const defaults = platform === 'iOS' ? DEFAULT_IOS_DEVICE_CONFIG : DEFAULT_ANDROID_DEVICE_CONFIG;
      clone.mobilePlatform = platform;
      clone.deviceConfig = { ...defaults, ...(clone.deviceConfig || {}) };
    }

    set({ steps: [...steps, clone] });
    logger.debug('Step added from source', { stepType: clone.stepType, alias });
    return clone;
  },

  duplicateStep: (stepId) =>
    set(state => {
      const idx = state.steps.findIndex(s => s.id === stepId);
      if (idx === -1) return state;
      const source = state.steps[idx];

      // For API steps, generate new sample file refs
      let clone = {
        ...JSON.parse(JSON.stringify(source)),
        id: `${source.id}-copy-${Date.now()}`,
        name: `${source.name} (Copy)`,
      };

      if (clone.stepType === 'Api' && clone.requests) {
        clone.requests = clone.requests.map(req => {
          const sharedId = generateId();
          return {
            ...req,
            sampleRequestContent: createSampleFileRef(state.projectPath, state.projectId, 'request', sharedId),
            sampleResponseContent: createSampleFileRef(state.projectPath, state.projectId, 'response', sharedId),
          };
        });
      }

      const next = [...state.steps];
      next.splice(idx + 1, 0, clone);
      return {
        steps: next.map((s, i) => ({ ...s, order: i + 1, alias: `step${i + 1}` })),
      };
    }),

  reorderStep: (fromIndex, toIndex) =>
    set(state => {
      if (fromIndex === toIndex) return state;
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= state.steps.length || toIndex >= state.steps.length) return state;
      const next = [...state.steps];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return {
        steps: next.map((s, i) => ({ ...s, order: i + 1, alias: `step${i + 1}` })),
      };
    }),

  // Move an entire group (all its steps) to a new position in the steps array.
  // targetIndex is the index where the group's first step should land after move.
  reorderGroup: (groupId, targetIndex) =>
    set(state => {
      const group = (state.stepGroups || []).find(g => g.id === groupId);
      if (!group) return state;
      const groupIdSet = new Set(group.stepIds);
      // Separate group steps (preserving their internal order) and the rest
      const groupSteps = state.steps.filter(s => groupIdSet.has(s.id));
      const rest = state.steps.filter(s => !groupIdSet.has(s.id));
      if (groupSteps.length === 0) return state;
      // Clamp target
      const clampedTarget = Math.max(0, Math.min(targetIndex, rest.length));
      // Insert group steps at the target position within the remaining steps
      const next = [...rest.slice(0, clampedTarget), ...groupSteps, ...rest.slice(clampedTarget)];
      return {
        steps: next.map((s, i) => ({ ...s, order: i + 1, alias: `step${i + 1}` })),
      };
    }),

  updateStep: (stepId, updates) =>
    set(state => ({
      steps: state.steps.map(s =>
        s.id === stepId ? { ...s, ...updates } : s
      ),
    })),

  /**
   * Put every Web-UI step in a session on the same browser.
   *
   * A sessionId is one browser window: the first step opens it and the rest continue in it, so
   * the steps cannot disagree about which browser that is. Changing one step's browser has to
   * move the whole session or the run silently executes some steps in a browser the report does
   * not name. Options the new browser refuses (blockAds on Firefox) are dropped per step at the
   * same time — leaving one behind would fail that step instead.
   */
  setSessionBrowserType: (sessionId, browserType) =>
    set(state => {
      if (!sessionId) return state;
      const resolved = normalizeWebUiBrowserType(browserType);
      return {
        steps: state.steps.map(s =>
          s.stepType === 'Web-UI' && s.sessionId === sessionId
            ? {
                ...s,
                browserType: resolved,
                browserOptions: stripUnsupportedBrowserOptions(s.browserOptions || {}, resolved),
              }
            : s
        ),
      };
    }),

  // ── Data Sources ──────────────────────────────────────────────

  updateDataSource: (dsId, updates) =>
    set(state => ({
      dataSources: state.dataSources.map(ds =>
        ds.id === dsId ? { ...ds, ...updates } : ds
      ),
    })),

  // ── Test Files ──────────────────────────────────────────────

  addTestFile: (file) =>
    set(state => ({ testFiles: [...state.testFiles, file] })),

  removeTestFile: (fileName) =>
    set(state => ({
      testFiles: state.testFiles.filter(f => f.name !== fileName),
    })),

  // ── Environments ──────────────────────────────────────────────

  addEnvironment: (name, baseUrl) => {
    const newEnv = {
      id: `env-${Date.now()}`,
      name,
      baseUrl,
      active: false,
    };
    set(state => {
      const isFirst = state.environments.length === 0;
      return {
        environments: [...state.environments, newEnv],
        // Auto-activate when it's the only environment
        activeEnvId: isFirst ? newEnv.id : state.activeEnvId,
      };
    });
  },

  /**
   * Rename an environment / change its base URL. The id is left alone so the
   * active selection survives the edit.
   */
  updateEnvironment: (envId, { name, baseUrl }) =>
    set(state => ({
      environments: state.environments.map(e =>
        e.id !== envId
          ? e
          : {
              ...e,
              ...(name !== undefined ? { name } : {}),
              ...(baseUrl !== undefined ? { baseUrl } : {}),
            }
      ),
    })),

  removeEnvironment: (envId) =>
    set(state => ({
      environments: state.environments.filter(e => e.id !== envId),
      activeEnvId: state.activeEnvId === envId ? null : state.activeEnvId,
    })),

  setActiveEnvironment: (envId) =>
    set({ activeEnvId: envId }),

  // ── Variables ─────────────────────────────────────────────────

  addVariable: (variable) => {
    const newVar = {
      id: `var-${Date.now()}`,
      name: variable.name || '',
      description: variable.description || '',
      source: variable.source || '',
      type: variable.type || 'Fixed',
      value: variable.value || '',
      value_type: variable.value_type || 'normal',
    };
    set(state => ({ variables: [...state.variables, newVar] }));
  },

  removeVariable: (varId) =>
    set(state => ({
      variables: state.variables.filter(v => v.id !== varId),
    })),

  updateVariable: (varId, updates) =>
    set(state => ({
      variables: state.variables.map(v =>
        v.id === varId ? { ...v, ...updates } : v
      ),
    })),

  // ── Data Source Columns ──────────────────────────────────────

  setDataSourceColumns: (requestColumns, responseColumns) =>
    set({ requestDataColumns: requestColumns, responseDataColumns: responseColumns }),

  // ── Step Groups ─────────────────────────────────────────────

  /**
   * Hydrate groups from grouped_steps.json (loaded from S3).
   * Called separately from hydrateFromPayload to keep request_payload untouched.
   */
  hydrateGroups: (groups) => {
    if (!groups || groups.length === 0) {
      set({ stepGroups: [] });
      return;
    }

    // Reconcile group stepIds with hydrated step IDs.
    // Older payloads may not have preserved step `id`, so the hydrated steps
    // use `alias` as their ID (e.g. "step2"), while grouped_steps.json still
    // references the original IDs (e.g. "step-grp-1773216109641-xxx").
    // We match via the alias stored in the group's step snapshots.
    const { steps: hydratedSteps } = get();
    const hydratedIdSet = new Set(hydratedSteps.map(s => s.id));

    const reconciled = groups.map(group => {
      const needsRemap = group.stepIds.some(id => !hydratedIdSet.has(id));
      if (!needsRemap) return group;

      // Build a mapping from old group stepId → hydrated step id via alias
      const newStepIds = [];
      const newSnapshots = [];
      for (let i = 0; i < group.stepIds.length; i++) {
        const oldId = group.stepIds[i];
        if (hydratedIdSet.has(oldId)) {
          // ID already matches — keep as-is
          newStepIds.push(oldId);
          if (group.steps?.[i]) newSnapshots.push(group.steps[i]);
          continue;
        }
        // Find the alias from the group's step snapshot
        const snapshot = group.steps?.[i];
        if (!snapshot) continue;
        const alias = snapshot.alias;
        // Find a hydrated step with a matching alias whose id differs
        const match = hydratedSteps.find(s => s.alias === alias || s.id === alias);
        if (match) {
          newStepIds.push(match.id);
          newSnapshots.push({ ...snapshot, id: match.id });
        } else {
          logger.warn('hydrateGroups: could not remap step', { oldId, alias });
        }
      }

      if (newStepIds.length < 2) return null; // Group dissolved

      return { ...group, stepIds: newStepIds, steps: newSnapshots };
    }).filter(Boolean);

    set({ stepGroups: reconciled });
    logger.debug('Step groups hydrated', { count: reconciled.length, remapped: reconciled.length !== groups.length || reconciled.some((g, i) => g !== groups[i]) });
  },

  /**
   * Create a new group from selected step IDs.
   * Snapshots the current step data for import-ability.
   */
  createGroup: (name, stepIds) => {
    const { steps, stepGroups } = get();
    const groupSteps = steps.filter(s => stepIds.includes(s.id));
    if (groupSteps.length < 2) {
      logger.warn('Cannot create group with fewer than 2 steps');
      return null;
    }

    // Build order mapping from global aliases to group-local aliases (step1, step2, ...)
    const groupOrderMapping = {};
    groupSteps.forEach((s, i) => {
      const globalOrder = parseInt(s.alias?.replace('step', ''), 10);
      if (!isNaN(globalOrder)) {
        groupOrderMapping[globalOrder] = i + 1;
      }
    });

    const uniqueName = ensureUniqueGroupName(name, stepGroups);
    const newGroup = {
      id: `grp-${generateId()}`,
      name: uniqueName,
      createdAt: new Date().toISOString(),
      stepIds: groupSteps.map(s => s.id),
      steps: groupSteps.map(s => {
        const clone = JSON.parse(JSON.stringify(s));
        // Remap variable refs from global aliases to group-local aliases
        return remapVarRefsInStep(clone, groupOrderMapping);
      }),
    };

    set({ stepGroups: [...stepGroups, newGroup] });
    logger.info('Step group created', { groupId: newGroup.id, name: uniqueName, stepCount: groupSteps.length });
    return newGroup;
  },

  /**
   * Remove a group definition (ungroup). Does NOT touch request_payload steps.
   */
  ungroupGroup: (groupId) =>
    set(state => ({
      stepGroups: state.stepGroups.filter(g => g.id !== groupId),
    })),

  /**
   * Update group name. Ensures uniqueness by appending _1, _2, etc.
   */
  renameGroup: (groupId, newName) =>
    set(state => {
      const otherGroups = state.stepGroups.filter(g => g.id !== groupId);
      const uniqueName = ensureUniqueGroupName(newName, otherGroups);
      return {
        stepGroups: state.stepGroups.map(g =>
          g.id === groupId ? { ...g, name: uniqueName } : g
        ),
      };
    }),

  /**
   * Duplicate a group: deep-clone all steps + register a new group definition.
   * Group name is auto-suffixed _1, _2, etc. to ensure uniqueness.
   */
  duplicateGroup: (groupId) => {
    const { steps, stepGroups, projectPath, projectId } = get();
    const group = stepGroups.find(g => g.id === groupId);
    if (!group) return null;

    const uniqueName = ensureUniqueGroupName(group.name, stepGroups);
    const newStepIds = [];
    const updatedSteps = [...steps];

    // Find the last step of the original group to insert clones right after
    const lastGroupStepIdx = Math.max(
      ...group.stepIds.map(id => steps.findIndex(s => s.id === id))
    );

    const clonedSteps = [];
    for (const stepId of group.stepIds) {
      const source = steps.find(s => s.id === stepId);
      if (!source) continue;

      let clone = {
        ...JSON.parse(JSON.stringify(source)),
        id: `step-grp-${Date.now()}-${generateId()}`,
      };

      // Regenerate sample file refs for API steps
      if (clone.stepType === 'Api' && clone.requests) {
        clone.requests = clone.requests.map(req => {
          const sharedId = generateId();
          return {
            ...req,
            sampleRequestContent: createSampleFileRef(projectPath, projectId, 'request', sharedId),
            sampleResponseContent: createSampleFileRef(projectPath, projectId, 'response', sharedId),
          };
        });
      }

      clonedSteps.push(clone);
      newStepIds.push(clone.id);
    }

    // Insert cloned steps right after the original group
    updatedSteps.splice(lastGroupStepIdx + 1, 0, ...clonedSteps);

    const newGroup = {
      id: `grp-${generateId()}`,
      name: uniqueName,
      createdAt: new Date().toISOString(),
      stepIds: newStepIds,
      steps: clonedSteps.map(s => JSON.parse(JSON.stringify(s))),
    };

    set({
      steps: updatedSteps.map((s, i) => ({ ...s, order: i + 1, alias: `step${i + 1}` })),
      stepGroups: [...stepGroups, newGroup],
    });

    logger.info('Group duplicated', { sourceGroupId: groupId, newGroupId: newGroup.id, name: uniqueName });
    return newGroup;
  },

  /**
   * Sync group snapshots with current step data.
   * Called before saving so imported groups always have fresh step data.
   */
  syncGroupSnapshots: () => {
    const { steps, stepGroups } = get();
    if (stepGroups.length === 0) return;

    const stepMap = new Map(steps.map(s => [s.id, s]));
    let changed = false;

    const updated = stepGroups.map(group => {
      // Remove stepIds that no longer exist
      const validIds = group.stepIds.filter(id => stepMap.has(id));
      if (validIds.length < 2) {
        changed = true;
        return null; // Group dissolved — not enough steps remain
      }

      const freshSteps = validIds.map(id => JSON.parse(JSON.stringify(stepMap.get(id))));
      const idsChanged = validIds.length !== group.stepIds.length;
      if (idsChanged) changed = true;

      return {
        ...group,
        stepIds: validIds,
        steps: freshSteps,
      };
    }).filter(Boolean);

    if (changed || updated.length !== stepGroups.length) {
      set({ stepGroups: updated });
    } else {
      // Always update snapshots (step content may have changed)
      set({ stepGroups: updated });
    }
  },

  /**
   * Import a group from another script: add all steps to request_payload
   * and register the group definition.
   */
  importGroup: (sourceGroup, newStepEntries) => {
    const { steps, stepGroups, projectPath, projectId } = get();
    const newStepIds = [];

    // Build order mapping from group-local aliases to new global aliases
    const importOrderMapping = {};
    const baseOrder = steps.length;
    newStepEntries.forEach((entry, i) => {
      const sourceOrder = parseInt(entry.alias?.replace('step', ''), 10);
      if (!isNaN(sourceOrder)) {
        importOrderMapping[sourceOrder] = baseOrder + i + 1;
      }
    });

    // Add each step as a new step in the pipeline
    const updatedSteps = [...steps];
    for (const entry of newStepEntries) {
      const newOrder = updatedSteps.length + 1;
      const alias = `step${newOrder}`;
      let clone = {
        ...JSON.parse(JSON.stringify(entry)),
        id: `step-grp-${Date.now()}-${generateId()}`,
        order: newOrder,
        alias,
      };

      // Remap variable refs from group-local aliases to new global aliases
      clone = remapVarRefsInStep(clone, importOrderMapping);

      // Regenerate sample file refs for API steps
      if (clone.stepType === 'Api' && clone.requests) {
        clone.requests = clone.requests.map(req => {
          const sharedId = generateId();
          return {
            ...req,
            sampleRequestContent: createSampleFileRef(projectPath, projectId, 'request', sharedId),
            sampleResponseContent: createSampleFileRef(projectPath, projectId, 'response', sharedId),
          };
        });
      }

      updatedSteps.push(clone);
      newStepIds.push(clone.id);
    }

    // Register the group with the new step IDs (ensure unique name)
    const uniqueName = ensureUniqueGroupName(sourceGroup.name, stepGroups);
    const newGroup = {
      id: `grp-${generateId()}`,
      name: uniqueName,
      createdAt: new Date().toISOString(),
      stepIds: newStepIds,
      steps: newStepIds.map((id, i) =>
        JSON.parse(JSON.stringify(updatedSteps.find(s => s.id === id)))
      ),
    };

    set({
      steps: updatedSteps,
      stepGroups: [...stepGroups, newGroup],
    });

    logger.info('Group imported', { groupName: sourceGroup.name, stepCount: newStepEntries.length });
    return { newGroup, newStepIds };
  },

  /**
   * Build the grouped_steps.json payload for S3.
   */
  buildGroupsPayload: () => {
    const { stepGroups } = get();
    return { groups: stepGroups };
  },

  // ── Settings ──────────────────────────────────────────────────

  toggleStopOnFailure: () =>
    set(state => ({ stopOnFailure: !state.stopOnFailure })),

  setScriptName: (name) =>
    set({ scriptName: name }),

  setProjectName: (name) =>
    set({ projectName: name }),

  /**
   * Set the authoritative S3 folder for this script and realign anything that
   * was addressed relative to the old one.
   *
   * The path embedded in request_payload.json is written at generation time by a
   * different helper than the one that creates the folder, so for auto-generated
   * scripts it can disagree with the real folder by casing (or by a dedup `_1`
   * suffix) — see TSB-4519. Every write in the editor resolves its upload folder
   * from `projectPath`, so a stale value silently creates a second script folder.
   * The loader therefore overrides it with the path the payload was actually read
   * from.
   *
   * Everything the payload addresses inside the script's own folder moves with it:
   * API sample-file s3Links, dataSource CSV paths and test-file links. Each is
   * rewritten only when it sits under the previous `projectPath` — anything
   * pointing elsewhere is left alone, so a payload that already agrees with the
   * real folder comes through completely unchanged.
   *
   * Rewriting the dataSource paths is what lets a script generated before this fix
   * heal itself: the run engine reads `dataSources[].content` as an absolute S3 key
   * and S3 is case-sensitive, so a wrong-cased path there resolves to nothing.
   */
  setProjectPath: (path) =>
    set(state => {
      const nextPath = path || '';
      const prevPath = state.projectPath || '';
      if (nextPath === prevPath) return {};
      if (!nextPath || !prevPath) return { projectPath: nextPath };

      // Rebase one absolute S3 key onto the new folder, or return it untouched.
      const rebase = (key) => (
        typeof key === 'string' && key.startsWith(`${prevPath}/`)
          ? `${nextPath}/${key.slice(prevPath.length + 1)}`
          : key
      );
      // Rewrite a field on an object only when rebasing actually changed it, so
      // untouched entries keep their identity.
      const rebaseField = (obj, field) => {
        if (!obj) return obj;
        const rebased = rebase(obj[field]);
        return rebased === obj[field] ? obj : { ...obj, [field]: rebased };
      };

      return {
        projectPath: nextPath,
        steps: state.steps.map(step => (
          step.stepType === 'Api' && Array.isArray(step.requests)
            ? {
                ...step,
                requests: step.requests.map(req => ({
                  ...req,
                  sampleRequestContent: rebaseField(req.sampleRequestContent, 's3Link'),
                  sampleResponseContent: rebaseField(req.sampleResponseContent, 's3Link'),
                })),
              }
            : step
        )),
        dataSources: state.dataSources.map(ds => rebaseField(ds, 'content')),
        testFiles: state.testFiles.map(file => rebaseField(file, 's3link')),
      };
    }),

  // ── Build Payload ─────────────────────────────────────────────

  /**
   * Reconstruct request_payload.json from current store state.
   * Used when saving back to S3.
   */
  buildPayload: () => {
    const state = get();

    // Recursively clean a single uiStep for the backend payload.
    // parentStepNumber is the 1-based number string of the Conditional parent (e.g. 5),
    // so sub-action stepNumbers become "5.1", "5.2" etc.
    // Keep only the condition fields that belong to each condition type's field
    // category, map identifierType to the backend enum, and drop empty values.
    // The category comes from CONDITION_TYPE_FIELDS (single source of truth in
    // automationConstants) so every UIConditionType serializes with the right shape.
    const CONDITION_FIELDS_BY_CATEGORY = {
      element:   ['identifierType', 'locator', 'timeout'],
      text:      ['identifierType', 'locator', 'expectedValue', 'timeout'],
      attribute: ['identifierType', 'locator', 'attribute', 'expectedValue', 'timeout'],
      count:     ['identifierType', 'locator', 'expectedValue', 'timeout'],
      page:      ['expectedValue'],
      alert:     ['timeout'],
      value:     ['variable', 'expectedValue'],
      actionRef: ['actionRef'],
    };

    const cleanCondition = (raw) => {
      if (!raw) return null;
      const cond = {};
      for (const [k, v] of Object.entries(raw)) {
        if (v === '' || v == null) continue;
        cond[k] = v;
      }
      // A condition with no type is incomplete/optional — omit it entirely so an
      // empty loop stopCondition (or unset IF condition) is never sent as a stray object.
      if (!cond.type) return null;
      // Coerce numeric fields
      if (cond.timeout != null) cond.timeout = Number(cond.timeout);
      if (cond.actionRef != null) cond.actionRef = Number(cond.actionRef);
      // Map identifierType to backend enum
      if (cond.identifierType && IDENTIFIER_TYPE_MAP[cond.identifierType]) {
        cond.identifierType = IDENTIFIER_TYPE_MAP[cond.identifierType];
      }
      // Strip fields irrelevant to this condition type's field category.
      // Unknown/blank types keep every field (defensive — never drops legacy data).
      const category = CONDITION_TYPE_FIELDS[cond.type];
      const allowed = category && CONDITION_FIELDS_BY_CATEGORY[category];
      if (allowed) {
        const keep = new Set(['type', ...allowed]);
        for (const k of Object.keys(cond)) {
          if (!keep.has(k)) delete cond[k];
        }
      }
      return Object.keys(cond).length > 0 ? cond : null;
    };

    const cleanUiStep = (action, idx, parentStepNumber = null) => {
      const actionType = action.action;
      const stepNumber = parentStepNumber != null
        ? `${parentStepNumber}.${idx + 1}`
        : idx + 1;

      const cleaned = { stepNumber };

      for (const [key, val] of Object.entries(action)) {
        // Strip transient UI fields and special keys handled separately below
        if (key === 'id' || key === 'timeoutUnit') continue;
        if (key === 'then' || key === 'else' || key === 'condition') continue;
        if (key === 'loop' || key === 'actions') continue;
        if (val === '' || val == null) continue;
        cleaned[key] = val;
      }

      if (cleaned.timeout) cleaned.timeout = Number(cleaned.timeout);

      if (cleaned.identifierType && IDENTIFIER_TYPE_MAP[cleaned.identifierType]) {
        cleaned.identifierType = IDENTIFIER_TYPE_MAP[cleaned.identifierType];
      }
      if (NO_LOCATOR_ACTIONS.has(actionType)) delete cleaned.identifierType;

      // Conditional-specific serialization. Both branch arrays are ALWAYS emitted
      // (even when empty) to match the engine's "Conditional inside a loop" contract
      // — then/else must be present, can be [] (same as the mobile engine).
      if (actionType === 'Conditional' && action.condition) {
        const cond = cleanCondition(action.condition);
        if (cond) cleaned.condition = cond;

        cleaned.then = (action.then || []).map((s, i) => cleanUiStep(s, i, stepNumber));
        cleaned.else = (action.else || []).map((s, i) => cleanUiStep(s, i, stepNumber));
      }

      // Break Loop / End Permutation are bare and unconditional — they fire when
      // reached, and a Conditional branch is what gates them. The engine no longer
      // evaluates a condition on the action, so any `condition` left on one by an
      // older payload is dropped here (the loop above already skips the key).

      // Loop-specific serialization (v2 web-UI shape: pattern / stopCondition)
      if (actionType === 'Loop' && action.loop) {
        const loop = {};
        // v2 pattern (retry|poll|scroll|custom) — required so the editor can
        // restore the correct loop panel after reload. Without it the UI falls
        // back to 'retry' and the user's custom loop config is lost.
        if (action.loop.pattern) loop.pattern = action.loop.pattern;
        // Legacy mobile-style type, only kept if present on older payloads.
        if (action.loop.type) loop.type = action.loop.type;
        // Always include maxIterations and delayMs
        loop.maxIterations = Number(action.loop.maxIterations ?? 10);
        loop.delayMs = Number(action.loop.delayMs ?? 0);

        // Scroll-pattern fields
        if (action.loop.maxScrolls !== '' && action.loop.maxScrolls != null) {
          loop.maxScrolls = Number(action.loop.maxScrolls);
        }
        if (action.loop.locator) loop.locator = action.loop.locator;
        if (action.loop.identifierType) {
          loop.identifierType = IDENTIFIER_TYPE_MAP[action.loop.identifierType] || action.loop.identifierType;
        }

        // Stop condition — v2 uses loop.stopCondition; fall back to legacy condition.
        const cond = cleanCondition(action.loop.stopCondition || action.loop.condition);
        if (cond) loop.stopCondition = cond;

        cleaned.loop = loop;

        // Loop body is always emitted as an array (even when empty) — matches the
        // engine contract and the mobile engine's serialization.
        cleaned.actions = (action.actions || []).map((s, i) => cleanUiStep(s, i, stepNumber));
      }

      return cleaned;
    };

    return {
      runid: '',
      timestamp: '',
      eventType: '',
      version: '',
      source: '',
      correlationId: '',
      tenantId: state.tenantId,
      projectId: state.projectId,
      runnableId: '',
      runnableType: '',
      projectName: state.projectName,
      s3Bucket: state.s3Bucket,
      projectPath: state.projectPath,
      logPath: '',
      reportPath: '',
      summaryPath: '',
      globalVariables: [],
      tests: [
        {
          name: state.scriptName,
          ...(state.variables.length > 0 && { variables: state.variables.map(({ id, ...v }) => v) }),
          ...(state.dataSources.length > 0 && { dataSources: state.dataSources.map(({ id, ...ds }) => ({ id, ...ds })) }),
          ...(state.testFiles.length > 0 && { testFiles: state.testFiles }),
          steps: state.steps.map(({ order: currentOrder, id: currentId, ...step }) => {
            const isMobileStep = step.stepType === 'Mobile';
            const isWebUiStep  = step.stepType === 'Web-UI';
            const isApiStep    = step.stepType === 'Api';

            // Normalize stepType casing for backend
            const stepType = isMobileStep ? 'mobile' : step.stepType;

            // Fields common to every step type
            const base = {
              name: step.name,
              alias: step.alias,
              stepType,
              ...(step.description && { description: step.description }),
              disabled: step.disabled,
              stopOnFailure: step.stopOnFailure,
              // Strip the UI-only `id` (added on hydrate/add) so saved JSON stays clean.
              variables: step.variables?.length > 0 ? step.variables.map(({ id, ...v }) => v) : undefined,
              ...(step.sessionId && { sessionId: step.sessionId }),
              ...(step.configureInStepFlow && { configureInStepFlow: true }),
            };

            if (isMobileStep) {
              // Strip empty-string capability values — Appium treats "" as a real capability and breaks session start.
              const rawCfg = step.deviceConfig || {};
              const cleanedCfg = Object.fromEntries(
                Object.entries(rawCfg).filter(([, v]) => v !== '' && v !== null && v !== undefined)
              );
              // Every mobile step must carry a deviceConfig with at least connectionType
              // so the backend validator passes, even on session-continuation steps.
              const connectionType = rawCfg.connectionType || (step.mobilePlatform === 'iOS' ? 'simulator' : 'emulator');
              const finalCfg = { connectionType, ...cleanedCfg };
              // If the step is in browser mode (appSourceType saved in store) but browserName
              // was never explicitly set, default to Chrome/Safari so Appium opens a real browser.
              if (step.appSourceType === 'browser' && !finalCfg.browserName) {
                finalCfg.browserName = step.mobilePlatform === 'iOS' ? DEFAULT_IOS_BROWSER : DEFAULT_ANDROID_BROWSER;
              }
              const cleanedMobileSteps = (step.mobileSteps || [])
                .map(({ id: _id, ...action }) => action)
                .map((a) => normalizeMobileActionForPlatform(a, step.mobilePlatform));
              return {
                ...base,
                mobilePlatform: step.mobilePlatform,
                deviceConfig: finalCfg,
                mobileSteps: cleanedMobileSteps,
              };
            }

            if (isWebUiStep) {
              // eslint-disable-next-line no-unused-vars
              const { hasPassed, hasExecutedSuccessfully, uiSteps, browserType, browserOptions, ...rest } = step;
              // Normalize so a legacy lowercase value ('chrome') — or the 'MOZILLA_FIREFOX'
              // the dropdown used to emit — is written back as the enum the engine expects.
              const resolvedBrowserType = normalizeWebUiBrowserType(browserType);
              return {
                ...base,
                browserType: resolvedBrowserType,
                // Last line of defence: an option the browser refuses (blockAds on Firefox)
                // fails the step, and a script saved before Firefox was selectable can still
                // be carrying one.
                browserOptions: stripUnsupportedBrowserOptions(browserOptions || {}, resolvedBrowserType),
                uiSteps: (uiSteps || []).map((action, idx) => cleanUiStep(action, idx)),
              };
            }

            if (isApiStep) {
              // eslint-disable-next-line no-unused-vars
              const { databaseConnection, query, queryType, extractedFields, requests, endpoint, listeners, listenerTimeoutSeconds, fieldsToExtract, method, disabled, ...rest } = step;
              return {
                ...base,
                disabled: step.disabled,
                method: step.method,
                endpoint: step.endpoint,
                requests: (requests || []).map(({ _reqBodyJson, _resBodyJson, ...req }) => req),
                listeners: listeners || [],
                listenerTimeoutSeconds: listenerTimeoutSeconds ?? 0,
                fieldsToExtract: fieldsToExtract || {},
              };
            }

            // Database and any other type — pass through without transient fields
            // eslint-disable-next-line no-unused-vars
            const { databaseConnection, query, queryType, extractedFields, hasPassed, hasExecutedSuccessfully, ...rest } = step;
            return { ...base, ...rest };
          }),
        },
      ],
      stopOnFailure: String(state.stopOnFailure),
    };
  },

  // ── Object Bank ────────────────────────────────────────────────

  hydrateObjectBank: (data) => {
    set({ objectBank: Array.isArray(data?.objectBank) ? data.objectBank : [] });
    logger.debug('Object bank hydrated', { pageCount: (data?.objectBank || []).length });
  },

  addBankPage: (page) => set((state) => ({
    objectBank: [...state.objectBank, page],
  })),

  removeBankPage: (pageId) => set((state) => ({
    objectBank: state.objectBank.filter((p) => p.id !== pageId),
  })),

  updateBankPage: (pageId, changes) => set((state) => ({
    objectBank: state.objectBank.map((p) => p.id === pageId ? { ...p, ...changes } : p),
  })),

  addBankElement: (pageId, element) => set((state) => ({
    objectBank: state.objectBank.map((p) =>
      p.id === pageId ? { ...p, elements: [...(p.elements || []), element] } : p
    ),
  })),

  updateBankElement: (pageId, elemId, changes) => set((state) => ({
    objectBank: state.objectBank.map((p) =>
      p.id === pageId
        ? { ...p, elements: (p.elements || []).map((e) => e.id === elemId ? { ...e, ...changes } : e) }
        : p
    ),
  })),

  removeBankElement: (pageId, elemId) => set((state) => ({
    objectBank: state.objectBank.map((p) =>
      p.id === pageId
        ? {
            ...p,
            elements: (p.elements || []).filter((e) => e.id !== elemId),
            businessProcesses: (p.businessProcesses || []).map((bp) => ({
              ...bp,
              elementIds: (bp.elementIds || []).filter((id) => id !== elemId),
            })),
          }
        : p
    ),
  })),

  addBankBusinessProcess: (pageId, process) => set((state) => ({
    objectBank: state.objectBank.map((p) =>
      p.id === pageId ? { ...p, businessProcesses: [...(p.businessProcesses || []), process] } : p
    ),
  })),

  updateBankBusinessProcess: (pageId, processId, changes) => set((state) => ({
    objectBank: state.objectBank.map((p) =>
      p.id === pageId
        ? {
            ...p,
            businessProcesses: (p.businessProcesses || []).map((bp) =>
              bp.id === processId ? { ...bp, ...changes } : bp
            ),
          }
        : p
    ),
  })),

  removeBankBusinessProcess: (pageId, processId) => set((state) => ({
    objectBank: state.objectBank.map((p) =>
      p.id === pageId
        ? { ...p, businessProcesses: (p.businessProcesses || []).filter((bp) => bp.id !== processId) }
        : p
    ),
  })),

  // ── Reset ─────────────────────────────────────────────────────

  /** Cleared by the page once the repaired script has been written back. */
  clearBrowserOptionRepairs: () => set({ browserOptionRepairs: [] }),

  reset: () => {
    set(INITIAL_STATE);
    logger.debug('Automation store reset');
  },
}));

// ── Granular Selector Hooks ─────────────────────────────────────

export const useAutomationSteps = () =>
  useAutomationStore(state => state.steps);

export const useAutomationDataSources = () =>
  useAutomationStore(state => state.dataSources);

export const useAutomationTestFiles = () =>
  useAutomationStore(state => state.testFiles);

export const useAutomationObjectBank = () =>
  useAutomationStore(state => state.objectBank);

export const useAutomationVariables = () =>
  useAutomationStore(state => state.variables);

export const useAutomationEnvironments = () =>
  useAutomationStore(state => state.environments);

export const useActiveEnvId = () =>
  useAutomationStore(state => state.activeEnvId);

export const useRequestDataColumns = () =>
  useAutomationStore(state => state.requestDataColumns);

export const useResponseDataColumns = () =>
  useAutomationStore(state => state.responseDataColumns);

export const useAutomationStepGroups = () =>
  useAutomationStore(state => state.stepGroups);

export const useAutomationStopOnFailure = () =>
  useAutomationStore(state => state.stopOnFailure);

export const useAutomationIsHydrated = () =>
  useAutomationStore(state => state.isHydrated);

export const useAutomationIsLoading = () =>
  useAutomationStore(state => state.isLoading);

export const useAutomationLoadingError = () =>
  useAutomationStore(state => state.loadingError);

export const useAutomationMeta = () =>
  useAutomationStore(
    useShallow(state => ({
      projectId: state.projectId,
      projectName: state.projectName,
      projectPath: state.projectPath,
      tenantId: state.tenantId,
      s3Bucket: state.s3Bucket,
      scriptName: state.scriptName,
    }))
  );

export const useAutomationActions = () =>
  useAutomationStore(
    useShallow(state => ({
      hydrateFromPayload: state.hydrateFromPayload,
      setLoading: state.setLoading,
      addStep: state.addStep,
      addStepFromSource: state.addStepFromSource,
      removeStep: state.removeStep,
      duplicateStep: state.duplicateStep,
      reorderStep: state.reorderStep,
      reorderGroup: state.reorderGroup,
      updateStep: state.updateStep,
      setSessionBrowserType: state.setSessionBrowserType,
      clearBrowserOptionRepairs: state.clearBrowserOptionRepairs,
      updateDataSource: state.updateDataSource,
      addTestFile: state.addTestFile,
      removeTestFile: state.removeTestFile,
      addEnvironment: state.addEnvironment,
      updateEnvironment: state.updateEnvironment,
      removeEnvironment: state.removeEnvironment,
      setActiveEnvironment: state.setActiveEnvironment,
      addVariable: state.addVariable,
      removeVariable: state.removeVariable,
      updateVariable: state.updateVariable,
      toggleStopOnFailure: state.toggleStopOnFailure,
      setScriptName: state.setScriptName,
      setProjectName: state.setProjectName,
      setProjectPath: state.setProjectPath,
      buildPayload: state.buildPayload,
      hydrateGroups: state.hydrateGroups,
      createGroup: state.createGroup,
      ungroupGroup: state.ungroupGroup,
      renameGroup: state.renameGroup,
      duplicateGroup: state.duplicateGroup,
      syncGroupSnapshots: state.syncGroupSnapshots,
      importGroup: state.importGroup,
      buildGroupsPayload: state.buildGroupsPayload,
      hydrateObjectBank: state.hydrateObjectBank,
      addBankPage: state.addBankPage,
      removeBankPage: state.removeBankPage,
      updateBankPage: state.updateBankPage,
      addBankElement: state.addBankElement,
      updateBankElement: state.updateBankElement,
      removeBankElement: state.removeBankElement,
      addBankBusinessProcess: state.addBankBusinessProcess,
      updateBankBusinessProcess: state.updateBankBusinessProcess,
      removeBankBusinessProcess: state.removeBankBusinessProcess,
      reset: state.reset,
    }))
  );